import { useAdsWorkspaceStore } from "../store/useAdsWorkspaceStore";
import { type DispatchContext } from "@/features/companion";
import { appEvents } from "@/shared/lib/events";
import { enqueueCursorMove } from "./marcusCursorQueue";

function emitAds(command: string, params: Record<string, unknown>): void {
  appEvents.publish({ type: "ads-ui-action", command, params });
}

/** Returns true if the tool was handled. */
export function dispatchAdsTool(toolName: string, ctx: DispatchContext): boolean {
  const { payload } = ctx;

  switch (toolName) {
    case "ads_navigate": {
      const p = (payload ?? {}) as { tab?: string; productName?: string; adId?: string };
      if (p.tab) {
        emitAds("switch_tab", { tab: p.tab });
      } else if (p.productName) {
        emitAds("open_product", { productName: p.productName });
      } else if (p.adId) {
        emitAds("open_ad_detail", { adId: p.adId });
      }
      return true;
    }
    case "ads_open_new_ad":
      emitAds("open_new_ad", payload);
      return true;
    case "ads_update_modal_field":
      emitAds("update_modal_field", payload);
      return true;
    case "ads_add_product": {
      emitAds("open_add_product", { method: "manual_entry" });
      const fields = payload ?? {};
      setTimeout(() => {
        emitAds("fill_product_fields", fields);
        setTimeout(() => {
          emitAds("submit_product", {});
        }, 60);
      }, 80);
      return true;
    }

    case "dispatch_scout_research": {
      const p = (payload ?? {}) as {
        sessionId?: string;
        productId?: string;
        productName?: string;
      };
      if (p.sessionId) {
        emitAds("open_scout_research", {
          sessionId: p.sessionId,
          ...(p.productId ? { productId: p.productId } : {}),
          ...(p.productName ? { productName: p.productName } : {}),
        });
      }
      return true;
    }

    case "ads_select_assets":
      emitAds("select_assets", payload);
      return true;

    case "ads_toggle_favorite":
      emitAds("toggle_favorite", payload);
      return true;

    case "ads_highlight": {
      const hp = (payload ?? {}) as { target?: string; comment?: string; pulses?: number };
      if (hp.target) {
        enqueueCursorMove({ target: hp.target, comment: hp.comment, pulses: hp.pulses });
      }
      return true;
    }

    case "ads_forecast_performance":
      useAdsWorkspaceStore.getState().setPerformanceForecasting(true);
      return true;

    case "ads_open_forecast_modal":
      emitAds("open_forecast_modal", payload);
      return true;

    case "ads_ui_announce_plan": {
      const ap = (payload ?? {}) as { steps?: Array<{ label: string; target?: string }> };
      if (Array.isArray(ap.steps) && ap.steps.length > 0) {
        appEvents.publish({ type: "marcus-plan-announced", steps: ap.steps });
      }
      return true;
    }

    case "ads_ui_plan_advance": {
      const adv = (payload ?? {}) as { target?: string; index?: number; labelHint?: string };
      appEvents.publish({ type: "marcus-plan-advance", ...adv });
      return true;
    }

    // ── Google Campaigns wizard walkthrough ─────────────────────────────────
    // Forwarded to the GoogleCampaignsPage's useGoogleWizardWalkthrough hook,
    // which sequences a cursor move + wizard-store mutation. Same envelope
    // shape as the rest of the ads-ui-action stream (`command` discriminator).

    case "gads_wizard_open":
      emitAds("gads_wizard_open", payload);
      return true;
    case "gads_wizard_goto_step":
      emitAds("gads_wizard_goto_step", payload);
      return true;
    case "gads_wizard_fill":
      emitAds("gads_wizard_fill", payload);
      return true;
    case "gads_wizard_submit":
      emitAds("gads_wizard_submit", payload);
      return true;

    default:
      return false;
  }
}
